'use client';

import { useState } from 'react';

import type { PracticeHotspot } from '@/entities/content';
import { productHref, type Product } from '@/entities/product';
import { formatMoney, rub } from '@/shared/lib/money';
import type { AppHref } from '@/shared/lib/route';
import { ButtonLink } from '@/shared/ui/button';
import { MediaFrame } from '@/shared/ui/media-frame';

import styles from './product-view.module.scss';

const CATALOG_BY_SPOT: Record<string, AppHref> = {
  body: '/catalog?direction=body',
  mind: '/catalog?direction=mind',
  atmo: '/catalog?direction=care',
};

function targetHref(spot: PracticeHotspot, product: Product | undefined): AppHref {
  if (spot.target === 'gift-set') return '/gift-set';
  if (spot.target === 'product' && product) return productHref(product);
  return CATALOG_BY_SPOT[spot.id] ?? '/catalog';
}

/**
 * "Продолжите практику" (03B-10): hotspots on the practice photo and a synced direction list.
 * Cards come from content (name, price, label, tint); a linked product only sets the target.
 */
export function PracticeHotspots({
  hotspots,
  products,
}: {
  hotspots: PracticeHotspot[];
  products: Product[];
}) {
  const [open, setOpen] = useState<string | null>(null);
  const active = hotspots.find((spot) => spot.id === open) ?? null;
  const productOf = (spot: PracticeHotspot) =>
    products.find((item) => item.slug === spot.productSlug);
  return (
    <div className={styles.practice}>
      <div className={styles.scene}>
        <MediaFrame
          src={null}
          alt=""
          sizes="60vw"
          className={styles['scene-photo']}
          placeholder={{
            label: 'Фото · практика',
            sub: 'утренний свет, коврик, свеча, ежедневник',
          }}
        />
        {hotspots.map((spot, index) => (
          <button
            key={spot.id}
            type="button"
            className={styles.hotspot}
            aria-expanded={spot.id === open}
            aria-label={`${spot.id === open ? 'Скрыть' : 'Показать'}: ${spot.title}`}
            style={{ '--x': `${spot.x}%`, '--y': `${spot.y}%` }}
            onClick={() => setOpen(spot.id === open ? null : spot.id)}
          >
            <span className={styles.dot} data-active={spot.id === open || undefined}>
              {index + 1}
            </span>
          </button>
        ))}
        {active && (
          <dialog
            open
            className={styles.hotcard}
            aria-label={active.name}
            style={{ '--x': `${active.cardX}%`, '--y': `${active.cardY}%` }}
          >
            <div className={styles.hothead}>
              <span className={styles.hoteyebrow}>
                {active.num} · {active.title}
              </span>
              <button
                type="button"
                className={styles.x}
                onClick={() => setOpen(null)}
                aria-label="Закрыть карточку"
              >
                ×
              </button>
            </div>
            <MediaFrame
              src={null}
              alt=""
              sizes="280px"
              tint={active.tint}
              placeholderStyle="hatch"
              className={styles.hotthumb}
              placeholder={{ label: active.label }}
            />
            <span className={styles.hotname}>{active.name}</span>
            <span className={styles.hotprice}>{formatMoney(rub(active.price))}</span>
            <ButtonLink
              href={targetHref(active, productOf(active))}
              variant="ghost"
              size="sm"
              className={styles.hotcta}
            >
              {active.cta}
            </ButtonLink>
          </dialog>
        )}
      </div>
      <div className={styles.dirs}>
        {hotspots.map((spot) => (
          <button
            key={spot.id}
            type="button"
            className={styles.dir}
            aria-pressed={spot.id === open}
            onClick={() => setOpen(spot.id)}
          >
            <span className={styles['dir-eyebrow']}>
              {spot.num} · {spot.title}
            </span>
            <span className={styles['dir-title']}>{spot.title}</span>
            <span className={styles['dir-text']}>{spot.text}</span>
            <span className={styles['dir-cta']}>{spot.directionCta}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
